import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { useToast } from '../components/Toast.jsx';
import { Wizard, WizardStep, isSafePublicHttpsUrl } from '../components/Wizard.jsx';
import {
  CheckIcon,
  DisableIcon,
  EnableIcon,
  DeleteIcon,
  AddIcon,
} from '../components/icons';

// Tenant-level events only.  Per-content events (tour.completed,
// content.viewed, etc.) live on each content detail page's Webhooks
// tab so the subscription is created in the right scope from the
// start — no copy-pasted content_id text input here.
const EVENT_OPTIONS = [
  {
    group: 'Pinpoint inbox',
    items: [
      // Order = lifecycle order, so the dropdown reads naturally
      // top-to-bottom (open → reply → assign → resolve → reopen → rate).
      // The legacy `pinpoint.thread.created` event is still accepted by
      // the backend allowlist (so existing subscriptions don't 400 on
      // update) but isn't offered as a fresh option — new subscribers
      // pick the equivalent `pinpoint.thread.opened`.
      { id: 'pinpoint.thread.opened',   label: 'New help request lands' },
      { id: 'pinpoint.thread.replied',  label: 'Help thread gets a reply' },
      { id: 'pinpoint.thread.assigned', label: 'Help thread assigned / re-assigned' },
      { id: 'pinpoint.thread.resolved', label: 'Help thread resolved' },
      { id: 'pinpoint.thread.reopened', label: 'Help thread re-opened' },
      { id: 'pinpoint.thread.rated',    label: 'User submitted a CSAT rating' },
    ],
  },
  {
    group: 'Team',
    items: [
      { id: 'member.invited',  label: 'Invite sent' },
      { id: 'member.joined',   label: 'Member accepted invite' },
      { id: 'member.removed',  label: 'Member removed' },
    ],
  },
  {
    group: 'Security',
    items: [
      { id: 'site.origin_blocked', label: 'Request blocked at origin lockdown' },
      { id: 'api_key.created',     label: 'API key minted' },
      { id: 'api_key.revoked',     label: 'API key revoked' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { id: 'sdk_version.bumped', label: 'SDK version re-tagged' },
    ],
  },
];

const DEFAULT_EVENT = EVENT_OPTIONS[0].items[0].id;

function labelForEvent(id) {
  for (const g of EVENT_OPTIONS) {
    for (const opt of g.items) if (opt.id === id) return opt.label;
  }
  return id;
}

/**
 * Tenant-wide outbound webhook subscriptions.  Per-content tabs on
 * each Tour / Content / Checklist detail page reuse the same
 * endpoints with a content_id filter.
 */
export function WebhookSubs() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [justMinted, setJustMinted] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [confirmNode, ask] = useConfirm();
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Tenant-wide page → tenant-level events only.  Per-content
      // subscriptions are scoped on the detail page they were
      // created on.
      const resp = await api('/v1/admin/webhook-subscriptions?tenant_wide_only=true');
      setSubs(resp?.subscriptions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Webhooks</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          POST a payload to your endpoint when something happens at
          the <strong>tenant level</strong> — a new help thread, a
          teammate joining, a request blocked at origin lockdown,
          an API key being minted or revoked. Each request carries
          an HMAC-SHA256 signature in <code>X-Spotlight-Signature</code> so
          your receiver can verify it really came from Spotlight before
          trusting the body.
        </p>
        <p style={{ color: 'var(--text-3)', margin: '8px 0 0', fontSize: 12.5 }}>
          Tour completions, content dismissals, and checklist
          progress events live on each tour / content / checklist's
          detail page — subscribe there so the subscription is bound
          to the right entity from the start.
        </p>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setShowWizard(true)}
        >
          <AddIcon /> Add webhook
        </button>
      </div>

      {showWizard && (
        <AddWebhookWizard
          api={api}
          onCancel={() => setShowWizard(false)}
          onCreated={(plain) => {
            setShowWizard(false);
            setJustMinted(plain);
            showToast({
              title: 'Webhook created',
              body: 'Save the signing secret below — it won\'t be shown again.',
              variant: 'success',
            });
            load();
          }}
        />
      )}

      {justMinted && <FreshSecret minted={justMinted} onDismiss={() => setJustMinted(null)} />}

      {loading && <div className="state"><div className="state__title">Loading…</div></div>}
      {error && !loading && <div className="state state--error">{error}</div>}

      {!loading && !error && subs.length === 0 && (
        <div className="state">
          <div className="state__title">No webhooks registered yet</div>
          <div>Use <strong>Add webhook</strong> above to register the first one. <a href="https://webhook.site" target="_blank" rel="noreferrer">webhook.site</a> is great for testing the signature handshake.</div>
        </div>
      )}

      {!loading && !error && subs.length > 0 && (
        <ul className="list">
          {subs.map((s) => (
            <SubRow key={s.subscription_id} s={s} api={api} onChanged={load} ask={ask} />
          ))}
        </ul>
      )}

      {confirmNode}
    </section>
  );
}

/**
 * Four-step "Add webhook" wizard (card 059).
 *
 *   1. Name + event picker (category grouped).
 *   2. Endpoint URL — HTTPS + SSRF guard, friendly inline validation.
 *   3. Test ping — skippable; HEAD-probes the URL to confirm
 *      reachability.  Skip → just persist the sub.
 *   4. Review — confirm before hitting the API.  Signing secret
 *      lands on the page after submit, never inside the wizard
 *      (and never in sessionStorage).
 */
function AddWebhookWizard({ api, onCancel, onCreated }) {
  const initial = {
    name: '',
    event: DEFAULT_EVENT,
    url: '',
    // Test ping is "pre-flight only" — we discard the result after the
    // operator reviews it.  Stored so the Review step can echo the
    // status in the summary.
    pingStatus: null,
    pingDetail: null,
  };

  async function pingUrl(url) {
    try {
      // No backend test endpoint today; do a CORS-safe HEAD probe so
      // the operator at least sees whether the URL resolves from a
      // browser.  no-cors hides the status, but a successful fetch
      // means the URL is at least reachable.
      const ctrl = new AbortController();
      const tid = window.setTimeout(() => ctrl.abort(), 6000);
      const resp = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: ctrl.signal,
      });
      window.clearTimeout(tid);
      return { ok: true, detail: resp?.type ? `response.type = ${resp.type}` : 'reachable' };
    } catch (ex) {
      return { ok: false, detail: ex?.message || 'fetch failed' };
    }
  }

  return (
    <Wizard
      id="add-webhook"
      title="Add a webhook"
      subtitle="Subscribe an endpoint to a tenant-level event."
      initialValues={initial}
      // Test-ping diagnostics are stale across reloads — strip them
      // from the draft so a refresh doesn't show a misleading "200 OK"
      // from a previous session.
      sensitiveFields={['pingStatus', 'pingDetail']}
      submitLabel="Add webhook"
      onCancel={onCancel}
      onSubmit={async (values) => {
        const resp = await api('/v1/admin/webhook-subscriptions', {
          method: 'POST',
          body: JSON.stringify({
            name: values.name.trim(),
            event: values.event,
            url: values.url.trim(),
          }),
        });
        onCreated(resp);
      }}
    >
      <WizardStep
        title="Name + event"
        isValid={(v) => v.name.trim().length > 0 && !!v.event}
      >
        {({ values, set }) => (
          <div className="wiz-stack">
            <label className="field">
              <span className="field__label">Name</span>
              <input
                className="input"
                value={values.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Mailchimp event sync"
              />
              <p className="wiz-hint">Internal label so you remember what this hook is for.</p>
            </label>
            <label className="field">
              <span className="field__label">Event</span>
              <select
                className="select"
                value={values.event}
                onChange={(e) => set('event', e.target.value)}
              >
                {EVENT_OPTIONS.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>
        )}
      </WizardStep>

      <WizardStep
        title="Endpoint URL"
        isValid={(v) => isSafePublicHttpsUrl(v.url.trim())}
      >
        {({ values, set }) => {
          const ok = isSafePublicHttpsUrl(values.url.trim());
          const hasValue = values.url.trim().length > 0;
          return (
            <div className="wiz-stack">
              <label className="field">
                <span className="field__label">Endpoint URL (HTTPS)</span>
                <input
                  type="url"
                  className="input"
                  value={values.url}
                  onChange={(e) => set('url', e.target.value)}
                  placeholder="https://hooks.example.com/spotlight"
                />
                {hasValue && !ok && (
                  <p className="wiz-hint" style={{ color: 'var(--danger-700)' }}>
                    HTTPS-only, and the host must be a public name (no
                    localhost / private IPs). The API will reject other
                    targets too — this is just an early warning.
                  </p>
                )}
                {!hasValue && (
                  <p className="wiz-hint">
                    The receiver verifies each request via the
                    <code> X-Spotlight-Signature </code> header. We sign
                    every payload with HMAC-SHA256.
                  </p>
                )}
              </label>
            </div>
          );
        }}
      </WizardStep>

      <WizardStep
        title="Test ping (optional)"
        optional
        isValid={(v) => v.pingStatus === 'ok'}
      >
        {({ values, set, busy }) => (
          <TestPingBody values={values} set={set} pingUrl={pingUrl} disabled={busy} />
        )}
      </WizardStep>

      <WizardStep title="Review">
        {({ values }) => (
          <div className="wiz-stack">
            <p className="wiz-hint" style={{ margin: 0 }}>
              We&apos;ll register this subscription and mint a one-time
              signing secret. Save the secret somewhere safe — we
              never show it again.
            </p>
            <dl className="wiz-review">
              <dt>Name</dt>
              <dd>{values.name || <em>untitled</em>}</dd>
              <dt>Event</dt>
              <dd>{labelForEvent(values.event)} <code style={{ fontSize: 11 }}>{values.event}</code></dd>
              <dt>Endpoint</dt>
              <dd><code>{values.url}</code></dd>
              {values.pingStatus && (
                <>
                  <dt>Test ping</dt>
                  <dd>
                    {values.pingStatus === 'ok'
                      ? `OK — ${values.pingDetail || 'reachable'}`
                      : `Failed — ${values.pingDetail || 'unknown'} (we'll still register the subscription)`}
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}
      </WizardStep>
    </Wizard>
  );
}

function TestPingBody({ values, set, pingUrl, disabled }) {
  const [pinging, setPinging] = useState(false);

  async function runPing() {
    setPinging(true);
    set('pingStatus', null);
    set('pingDetail', null);
    const { ok, detail } = await pingUrl(values.url.trim());
    set('pingStatus', ok ? 'ok' : 'fail');
    set('pingDetail', detail);
    setPinging(false);
  }

  return (
    <div className="wiz-stack">
      <p className="wiz-hint" style={{ margin: 0 }}>
        We&apos;ll send a quick HEAD probe to <code>{values.url}</code> from
        your browser to confirm the endpoint is at least reachable.
        This step is optional — skip it if your endpoint only accepts
        signed POSTs.
      </p>
      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={runPing}
          disabled={disabled || pinging || !isSafePublicHttpsUrl(values.url.trim())}
        >
          {pinging ? 'Pinging…' : (values.pingStatus ? 'Re-run ping' : 'Send test ping')}
        </button>
        {values.pingStatus === 'ok' && (
          <span className="badge badge--editor">
            <CheckIcon /> Reachable — {values.pingDetail || 'ok'}
          </span>
        )}
        {values.pingStatus === 'fail' && (
          <span className="badge badge--admin" title={values.pingDetail || ''}>
            Couldn&apos;t reach — {values.pingDetail || 'unknown'}
          </span>
        )}
      </div>
      <p className="wiz-hint">
        A failure here doesn&apos;t block creation — you can still register
        the subscription and fix routing afterwards. The button below
        will read &quot;Skip&quot; while the ping hasn&apos;t succeeded.
      </p>
    </div>
  );
}

function FreshSecret({ minted, onDismiss }) {
  return (
    <div className="state" style={{ background: '#fef9c3', borderColor: '#fde047' }}>
      <div className="state__title">Save this signing secret — it won&apos;t be shown again</div>
      <p style={{ margin: '6px 0', fontSize: 13 }}>
        Your receiver verifies the signature with this. Sign each request as
        <code> HMAC-SHA256(secret, &#123;timestamp&#125;.&#123;body&#125;)</code> and
        compare against the <code>v1=</code> half of the
        <code>X-Spotlight-Signature</code> header.
      </p>
      <code style={{ display: 'block', padding: 10, background: 'var(--surface-0)', borderRadius: 8, wordBreak: 'break-all' }}>{minted.secret}</code>
      <button type="button" className="btn btn--ghost" style={{ marginTop: 10 }} onClick={onDismiss}>
        <CheckIcon /> I&apos;ve saved it
      </button>
    </div>
  );
}

function SubRow({ s, api, onChanged, ask }) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    setBusy(true);
    try {
      await api(`/v1/admin/webhook-subscriptions/${encodeURIComponent(s.subscription_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !s.active }),
      });
      onChanged();
    } finally { setBusy(false); }
  }
  function del() {
    ask({
      title: `Delete "${s.name || s.subscription_id}"?`,
      body: 'Receiver stops getting events immediately.',
      confirmLabel: 'Delete',
      danger: true,
      runOnConfirm: async () => {
        await api(
          `/v1/admin/webhook-subscriptions/${encodeURIComponent(s.subscription_id)}`,
          { method: 'DELETE' },
        );
        onChanged();
      },
    });
  }
  return (
    <li className="list__row">
      <div className="list__primary">
        <span className="list__name">
          {s.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
          <span className="badge badge--viewer" style={{ marginLeft: 8 }}>{s.event}</span>
          {!s.active && <span className="badge badge--default" style={{ marginLeft: 6 }}>paused</span>}
        </span>
        <span className="list__meta">
          <code>{s.url}</code>
        </span>
        <span className="list__meta" style={{ marginTop: 4 }}>
          <code>{s.subscription_id}</code>
        </span>
      </div>
      <div className="list__actions">
        <button type="button" className="btn btn--ghost" onClick={toggle} disabled={busy}>
          {s.active ? <DisableIcon /> : <EnableIcon />}
          {s.active ? 'Pause' : 'Resume'}
        </button>
        <button type="button" className="btn btn--danger-ghost" onClick={del} disabled={busy}>
          <DeleteIcon /> Delete
        </button>
      </div>
    </li>
  );
}
